import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AdminNotifications } from '../../components/AdminNotifications';
import { 
  Users, FileText, DollarSign, TrendingUp, 
  Plus, LogOut, Home, Settings, BarChart3,
  ChevronRight, AlertCircle
} from 'lucide-react';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (loading) return; // Wait for auth check to complete
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [isAdmin, loading, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Administration | CAMBC Conseils</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100">
        {/* Top Navigation */}
        <div className="bg-slate-900 text-white py-4 px-6 fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white">
                <Home size={20} />
              </Link>
              <span className="text-slate-500">|</span>
              <h1 className="text-xl font-bold">Administration</h1>
            </div>
            <div className="flex items-center gap-4">
              <AdminNotifications />
              <span className="text-slate-400">{user?.email}</span>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <LogOut size={16} className="mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>

        <div className="pt-24 pb-12 px-6">
          <div className="max-w-7xl mx-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Apporteurs</p>
                      <p className="text-3xl font-bold text-slate-900">{stats?.total_apporteurs || 0}</p>
                      <p className="text-xs text-green-600 mt-1">{stats?.active_apporteurs || 0} actifs</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="text-blue-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Contrats</p>
                      <p className="text-3xl font-bold text-slate-900">{stats?.total_contracts || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Commissions en attente</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {(stats?.total_commissions_pending || 0).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-orange-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Commissions payées</p>
                      <p className="text-3xl font-bold text-green-600">
                        {(stats?.total_commissions_paid || 0).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Link to="/admin/apporteurs">
                <Card className="bg-white shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Users className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Apporteurs</p>
                        <p className="text-sm text-slate-500">Gérer les comptes</p>
                      </div>
                    </div>
                    <ChevronRight className="text-slate-400" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/admin/contracts">
                <Card className="bg-white shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                        <FileText className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Gérer les Contrats</p>
                        <p className="text-sm text-slate-500">Ajouter, éditer les contrats</p>
                      </div>
                    </div>
                    <ChevronRight className="text-slate-400" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/admin/commissions">
                <Card className="bg-white shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                        <DollarSign className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Commissions</p>
                        <p className="text-sm text-slate-500">Bordereaux, paiements</p>
                      </div>
                    </div>
                    <ChevronRight className="text-slate-400" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/admin/leads">
                <Card className="bg-white shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Users className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Leads</p>
                        <p className="text-sm text-slate-500">Prospects apporteurs</p>
                      </div>
                    </div>
                    <ChevronRight className="text-slate-400" />
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Contracts */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Derniers Contrats</span>
                    <Link to="/admin/contracts">
                      <Button variant="outline" size="sm">
                        <Plus size={16} className="mr-1" /> Nouveau
                      </Button>
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_contracts?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_contracts.map((contract) => (
                        <div key={contract.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-slate-900">{contract.client_name}</p>
                            <p className="text-sm text-slate-500">{contract.insurance_type} - {contract.apporteur_name}</p>
                          </div>
                          <span className="text-sm font-medium text-blue-600">
                            {contract.apporteur_commission?.toLocaleString('fr-FR')} €
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Aucun contrat récent</p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Payments */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Commissions en Attente</span>
                    <Link to="/admin/commissions">
                      <Button variant="outline" size="sm">Voir tout</Button>
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.pending_payments?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.pending_payments.map((commission) => (
                        <div key={commission.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-slate-900">{commission.apporteur_name}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(commission.period_start).toLocaleDateString('fr-FR')} - {new Date(commission.period_end).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <span className={`text-sm font-medium px-2 py-1 rounded ${
                            commission.status === 'en_attente' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {commission.amount?.toLocaleString('fr-FR')} €
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Aucune commission en attente</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
